import { useEffect, useRef } from 'react';
import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  favorites: EbookTemplate[];
  onUse: (t: EbookTemplate) => void;
  onPreview: (t: EbookTemplate) => void;
  onRemove: (id: string) => void;
  onShare: (t: EbookTemplate) => void;
}

const BADGE_STYLES: Record<string, string> = {
  Popular: 'bg-amber-100 text-amber-700',
  New: 'bg-emerald-100 text-emerald-700',
  Trending: 'bg-rose-100 text-rose-700',
};

export default function FavoritesDrawer({
  isOpen,
  onClose,
  favorites,
  onUse,
  onPreview,
  onRemove,
  onShare,
}: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape' || (e.key === 'f' || e.key === 'F')) {
        if (e.key === 'Escape' || isOpen) onClose();
      }
    };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, isOpen]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 bottom-0 z-[60] w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-50 border border-rose-100 rounded-xl flex items-center justify-center">
              <i className="ri-heart-fill text-rose-500 text-lg"></i>
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-900">My Favorites</h3>
              <p className="text-xs text-slate-400">
                {favorites.length === 0 ? 'No saved templates' : `${favorites.length} template${favorites.length !== 1 ? 's' : ''} saved`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {favorites.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center h-full px-8 text-center">
              <div className="w-20 h-20 bg-rose-50 rounded-3xl flex items-center justify-center mb-5">
                <i className="ri-heart-line text-rose-300 text-4xl"></i>
              </div>
              <h4 className="text-lg font-extrabold text-slate-800 mb-2">No favorites yet</h4>
              <p className="text-sm text-slate-500 leading-relaxed mb-6">
                Browse templates and click the{' '}
                <i className="ri-heart-line text-rose-500"></i>{' '}
                heart icon to save your favorites here.
              </p>
              <button
                onClick={onClose}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-compass-3-line text-sm"></i>
                Browse Templates
              </button>
            </div>
          ) : (
            <div className="p-5">
              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  {
                    icon: 'ri-heart-fill',
                    color: 'text-rose-500',
                    bg: 'bg-rose-50',
                    val: favorites.length,
                    label: 'Saved',
                  },
                  {
                    icon: 'ri-star-fill',
                    color: 'text-amber-500',
                    bg: 'bg-amber-50',
                    val: favorites.length > 0
                      ? (favorites.reduce((s, t) => s + t.rating, 0) / favorites.length).toFixed(1)
                      : '—',
                    label: 'Avg. Rating',
                  },
                  {
                    icon: 'ri-grid-line',
                    color: 'text-indigo-500',
                    bg: 'bg-indigo-50',
                    val: new Set(favorites.map(t => t.category)).size,
                    label: 'Genres',
                  },
                ].map(stat => (
                  <div key={stat.label} className={`${stat.bg} rounded-xl p-3 text-center`}>
                    <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
                      <i className={`${stat.icon} ${stat.color} text-base`}></i>
                    </div>
                    <p className="text-lg font-extrabold text-slate-900">{stat.val}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Template Grid */}
              <div className="grid grid-cols-2 gap-3">
                {favorites.map(template => (
                  <div
                    key={template.id}
                    className="group bg-white rounded-2xl border-2 border-slate-200 hover:border-rose-300 hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col cursor-pointer"
                    onClick={() => onPreview(template)}
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden flex-shrink-0" style={{ aspectRatio: '3/4', maxHeight: '160px' }}>
                      <img
                        src={template.image}
                        alt={template.name}
                        className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                      {/* Badge */}
                      {template.badge && (
                        <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold ${BADGE_STYLES[template.badge] || ''}`}>
                          {template.badge}
                        </div>
                      )}

                      {/* Remove button */}
                      <button
                        onClick={e => { e.stopPropagation(); onRemove(template.id); }}
                        title="Remove from favorites"
                        className="absolute top-2 right-2 w-7 h-7 bg-white/90 backdrop-blur-sm border border-white/50 rounded-full flex items-center justify-center text-rose-500 hover:bg-rose-500 hover:text-white transition-all cursor-pointer opacity-0 group-hover:opacity-100"
                      >
                        <i className="ri-heart-fill text-xs"></i>
                      </button>

                      {/* Color bar */}
                      <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: template.color }}></div>
                    </div>

                    {/* Info */}
                    <div className="p-3 flex-1 flex flex-col">
                      <h4 className="text-xs font-extrabold text-slate-900 group-hover:text-rose-600 transition-colors line-clamp-2 leading-snug mb-1">
                        {template.name}
                      </h4>
                      <p className="text-xs text-slate-400 mb-2 truncate">{template.category}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          {template.rating}
                        </span>
                        <span className="text-slate-300">·</span>
                        <span>{template.chapters} ch.</span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1.5 mt-auto">
                        <button
                          onClick={e => { e.stopPropagation(); onUse(template); }}
                          className="flex-1 py-1.5 rounded-lg text-xs font-bold text-white transition-all cursor-pointer hover:opacity-90 whitespace-nowrap"
                          style={{ backgroundColor: template.color }}
                        >
                          Use
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); onShare(template); }}
                          title="Share"
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:border-amber-300 hover:text-amber-600 transition-all cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-share-line text-xs"></i>
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); onRemove(template.id); }}
                          title="Remove"
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:border-rose-300 hover:text-rose-500 transition-all cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-delete-bin-6-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {favorites.length > 0 && (
          <div className="flex-shrink-0 border-t border-slate-100 px-5 py-4 flex items-center gap-3">
            <button
              onClick={() => favorites.forEach(t => onRemove(t.id))}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-500 text-sm font-semibold hover:border-rose-300 hover:text-rose-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-6-line text-sm"></i>
              Clear All
            </button>
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 text-white text-sm font-bold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-compass-3-line text-sm"></i>
              Browse More Templates
            </button>
          </div>
        )}
      </div>
    </>
  );
}
